import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

function ForgotPassword() {
  const location = useLocation();
  const resetToken = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [debugResetUrl, setDebugResetUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('');
    setError('');
    setDebugResetUrl('');
    setIsLoading(true);
    try {
      if (resetToken) {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match.');
        }
        await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
          token: resetToken,
          password,
        });
        setStatus('Password updated. You can now sign in.');
      } else {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/request-password-reset`, { email });
        setStatus(response.data?.message || 'If the email belongs to an active account, a reset link has been sent.');
        setDebugResetUrl(response.data?.debugResetUrl || '');
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Password reset failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page page-shell">
      <div className="auth-card section-card">
        <span className="tag">Reset access</span>
        <h1 className="page-title">{resetToken ? 'Set a new password' : 'Forgot your password?'}</h1>
        <p className="page-lead">
          {resetToken
            ? 'Choose a new password for your account.'
            : 'Enter your account email and we will send a secure reset link.'}
        </p>

        <form onSubmit={handleSubmit} className="form-grid">
          {resetToken ? (
            <>
              <div className="form-group">
                <label htmlFor="reset-password">New password</label>
                <input
                  id="reset-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="reset-confirm-password">Confirm password</label>
                <input
                  id="reset-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Repeat your new password"
                  minLength={8}
                  required
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@domain.com"
                required
              />
            </div>
          )}
          {status ? <p className="auth-success">{status}</p> : null}
          {debugResetUrl ? (
            <p className="auth-debug">
              {/* Local test link: <a href={debugResetUrl}>{debugResetUrl}</a> */}
            </p>
          ) : null}
          {error ? <p className="auth-error">{error}</p> : null}
          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Working...' : resetToken ? 'Update Password' : 'Send Reset Link'}
          </button>
        </form>

        <p className="auth-footer">
          Remembered your login? <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;
